import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Phone, Chrome, ArrowLeft, Eye, EyeOff, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login illustration URL
const LOGIN_ILLUSTRATION = "https://static.prod-images.emergentagent.com/jobs/b4e419ab-e302-44b7-9ad7-697c067738c2/images/72a49a77787c314343023db664c035199987dd279cd6b5462d941d42d76a6af0.png";

const AdminLogin = ({ setUser }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('email');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Email login state
  const [email, setEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  
  // Phone login state
  const [phone, setPhone] = useState('');
  const [phonePassword, setPhonePassword] = useState('');
  
  // Google login state
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/admin/email-login`, {
        email: email.trim().toLowerCase(),
        password: emailPassword
      });
      
      if (response.data.success) {
        // Store token and user data
        localStorage.setItem('authToken', response.data.token);
        
        // Set user with admin role
        const userData = {
          ...response.data.user,
          role: 'admin',
          token: response.data.token
        };
        
        setUser(userData);
        toast.success(`Welcome back, ${response.data.user.business_name}!`);
      }
    } catch (error) {
      console.error('Email login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Format phone number (add +91 if not present)
      let formattedPhone = phone.trim();
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+91' + formattedPhone.replace(/^0+/, '');
      }
      
      const response = await axios.post(`${API}/auth/admin/phone-login`, {
        phone: formattedPhone,
        password: phonePassword
      });
      
      if (response.data.success) {
        localStorage.setItem('authToken', response.data.token);
        
        const userData = {
          ...response.data.user,
          role: 'admin',
          token: response.data.token
        };
        
        setUser(userData);
        toast.success(`Welcome back, ${response.data.user.business_name}!`);
      }
    } catch (error) {
      console.error('Phone login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      // Use Emergent Google Auth - redirect to auth.emergentagent.com
      const redirectUrl = window.location.origin + '/admin-dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Google login is not available at the moment.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-muted to-background">
      
      {/* Main Container */}
      <div className="w-full max-w-md flex items-center justify-center">

        {/* Login Card */}
        <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 flex-shrink-0" data-testid="admin-login-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <img src="/fos-logo.png" alt="FOS Logo" className="h-16 sm:h-20 w-auto object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold text-primary tracking-tight">Admin Login</CardTitle>
            <CardDescription className="text-sm mt-1">Sign in to manage your franchise operations</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6 h-12 bg-muted/50 rounded-xl p-1">
                <TabsTrigger 
                  value="email" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-md flex items-center gap-1.5 text-sm"
                  data-testid="email-tab"
                >
                  <Mail className="h-4 w-4" />
                  <span className="hidden sm:inline">Email</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="phone" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-md flex items-center gap-1.5 text-sm"
                  data-testid="phone-tab"
                >
                  <Phone className="h-4 w-4" />
                  <span className="hidden sm:inline">Phone</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="google" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-md flex items-center gap-1.5 text-sm"
                  data-testid="google-tab"
                >
                  <Chrome className="h-4 w-4" />
                  <span className="hidden sm:inline">Google</span>
                </TabsTrigger>
              </TabsList>

              {/* Email Login Tab */}
              <TabsContent value="email" className="mt-0">
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-email" className="text-sm font-medium">Email Address</Label>
                    <Input
                      id="admin-email"
                      data-testid="admin-email-input"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 rounded-xl border-2 focus:border-primary"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="admin-email-password" className="text-sm font-medium">Password</Label>
                      <a href="/forgot-password" className="text-xs text-primary hover:underline">
                        Forgot Password?
                      </a>
                    </div>
                    <div className="relative">
                      <Input
                        id="admin-email-password"
                        data-testid="admin-email-password-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={emailPassword}
                        onChange={(e) => setEmailPassword(e.target.value)}
                        className="h-12 rounded-xl border-2 focus:border-primary pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="email-login-button"
                    disabled={loading}
                    className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full text-base font-bold uppercase tracking-wider mt-4"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Signing in...
                      </span>
                    ) : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              {/* Phone Login Tab */}
              <TabsContent value="phone" className="mt-0">
                <form onSubmit={handlePhoneLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-phone" className="text-sm font-medium">Phone Number</Label>
                    <div className="flex gap-2">
                      <div className="flex items-center justify-center px-3 h-12 bg-muted rounded-xl border-2 text-sm font-medium text-muted-foreground">
                        +91
                      </div>
                      <Input
                        id="admin-phone"
                        data-testid="admin-phone-input"
                        type="tel"
                        placeholder="9876543210"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="h-12 rounded-xl border-2 focus:border-primary flex-1"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-phone-password" className="text-sm font-medium">Password</Label>
                    <div className="relative">
                      <Input
                        id="admin-phone-password"
                        data-testid="admin-phone-password-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={phonePassword}
                        onChange={(e) => setPhonePassword(e.target.value)}
                        className="h-12 rounded-xl border-2 focus:border-primary pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="phone-login-button"
                    disabled={loading}
                    className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full text-base font-bold uppercase tracking-wider mt-4"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Signing in...
                      </span>
                    ) : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              {/* Google Login Tab */}
              <TabsContent value="google" className="mt-0">
                <div className="space-y-6 py-4">
                  <div className="text-center space-y-2">
                    <Chrome className="h-12 w-12 mx-auto text-primary opacity-80" />
                    <p className="text-sm text-muted-foreground">
                      Sign in with your Google account linked to FOS
                    </p>
                  </div>
                  
                  <Button
                    type="button"
                    onClick={handleGoogleLogin}
                    data-testid="google-login-button"
                    disabled={googleLoading}
                    variant="outline"
                    className="w-full h-12 rounded-full text-base font-medium border-2 hover:bg-muted flex items-center justify-center gap-3"
                  >
                    {googleLoading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Connecting...
                      </span>
                    ) : (
                      <>
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    Google login requires your account to be linked by the administrator
                  </p>
                </div>
              </TabsContent>
            </Tabs>
            
            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">
                  New to FOS?
                </span>
              </div>
            </div>
            
            {/* Contact Admin */}
            <p className="text-center text-sm text-muted-foreground">
              Contact your administrator to get started or{' '}
              <a href="https://getfos.in" className="text-primary font-medium hover:underline">
                visit our website
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
